import classNames from 'classnames';
import { formatCurrency } from 'lib/helpers/strings';
import { User } from 'modules/App/interfaces';
import CopyButton from 'modules/common/components/CopyButton';
import Areas from 'modules/geo/components/Areas';
import CountryEmojis from 'modules/geo/data-components/CountryEmojis';
import { Link } from 'modules/i18n';
import { companySizes } from 'modules/User/models/constants';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';
import { FaLinkedin as IconLinkedIn } from 'react-icons/fa';
import { MdEmail as IconEmail, MdLocalPhone as IconPhone, MdLocationOn as IconLocation } from 'react-icons/md';
import BadgeHolidayTooltip from './BadgeHolidayTooltip';
import UserStats from './UserStats';

export type MemberDetailsProps = {
  member: User;
  openDescription?: boolean;
} & React.HTMLAttributes<HTMLDListElement>;

const MemberDetails: React.FC<MemberDetailsProps> = ({ children, className, openDescription = false, member, ...props }) => {
  const router = useRouter();
  const {
    name,
    url,
    email,
    image,
    companySize,
    companyLocation,
    sector,
    areaSearch,
    countryIds,
    occupation,
    companyClients,
    companySalaryTreated,
    businessRegistrationId,
    bio,
    phone,
    title,
  } = member;

  return (
    <main className={classNames('min-h-full', className)} {...props}>
      {/* Page header */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:flex lg:items-center lg:justify-between lg:space-x-5 lg:max-w-7xl lg:px-8">
        <div className="flex-[3_3_0%] sm:flex items-center sm:space-x-5 flex">
          <div className="flex-shrink-0 relative">
            <Image
              width="128"
              height="128"
              className="rounded-full"
              src={image || `https://placehold.co/52?text=${name?.charAt(0).toUpperCase()}`}
              alt={name || ''}
            />
            <span className="absolute inset-0 sm:shadow-inner rounded-full" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 pl-0">{name}</h1>
            <p className="text-sm font-medium text-gray-500">
              <span>{title}</span>
              <span className="text-gray-900 flex items-center -ml-1">
                <IconLocation /> {companyLocation}
              </span>
            </p>
            <BadgeHolidayTooltip user={member} />
            <UserStats user={member} />
          </div>
        </div>
        <div className="flex-1 mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 lg:mt-0 lg:flex-row lg:space-x-3">
          <Link
            href={`/searched-profiles?q=${name}`}
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
          >
            Voir ses missions
          </Link>
        </div>
      </div>
      <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 lg:mt-0 lg:flex-row lg:space-x-3"></div>
      <div className="mt-8 max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl">
        <div className="space-y-6 lg:col-start-1 lg:col-span-2">
          <section aria-labelledby="headhunter-information-title">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="flex flex-wrap justify-center md:justify-end items-center gap-2 mx-2">
                <a
                  type="button"
                  href={url}
                  target="_blank"
                  className="inline-flex items-center justify-center px-4 py-2 my-1 sm:my-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
                  rel="noreferrer"
                >
                  <IconLinkedIn />
                  LinkedIn
                </a>
                <a
                  type="button"
                  href={`tel://${phone}`}
                  target="_blank"
                  className="inline-flex items-center justify-center px-4 py-2 my-1 sm:my-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
                  rel="noreferrer"
                >
                  <IconPhone />
                  {phone}
                </a>
                <CopyButton
                  className="inline-flex items-center justify-center px-4 py-2 my-1 sm:my-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
                  value={phone}
                >
                  Tel
                </CopyButton>
                <a
                  type="button"
                  href={`mailto:${email}?subject=${encodeURIComponent(`Contact Headlinker`)}`}
                  target="_blank"
                  className="inline-flex items-center justify-center px-4 py-2 my-1 sm:my-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
                  rel="noreferrer"
                >
                  <IconEmail />
                  {email}
                </a>
                <CopyButton
                  className="inline-flex items-center justify-center px-4 py-2 my-1 sm:my-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 gap-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500"
                  value={email}
                >
                  Email
                </CopyButton>
              </div>
              <div className="border-t border-gray-50 px-4 py-5 sm:px-6">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Expertises</dt>
                    <dd className="mt-1 text-sm text-gray-900">{sector}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Métiers</dt>
                    <dd className="mt-1 text-sm text-gray-900">{occupation}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Clients</dt>
                    <dd className="mt-1 text-sm text-gray-900">{companyClients || <em>Confidentiel</em>}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Couverture Géographique</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <CountryEmojis countryIds={countryIds || []} />
                      <Areas items={areaSearch?.areas || []} />
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Taille du cabinet</dt>
                    <dd className="mt-1 text-sm text-gray-900">{companySizes[companySize as keyof typeof companySizes] || companySize}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">Salaires traités</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatCurrency({ locale: router.locale, value: +companySalaryTreated?.min, compact: true })} à{' '}
                      {formatCurrency({ locale: router.locale, value: +companySalaryTreated?.max, compact: true })}
                    </dd>
                  </div>
                  {businessRegistrationId && (
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">SIRET/SIREN</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {[9, 14].includes(businessRegistrationId.length) ? (
                          <a
                            className="link"
                            href={`https://www.societe.com/cgi-bin/search?champs=${businessRegistrationId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {businessRegistrationId}
                          </a>
                        ) : (
                          businessRegistrationId
                        )}
                      </dd>
                    </div>
                  )}

                  {bio && (
                    <div className="sm:col-span-2">
                      <details className="group" {...(openDescription ? { open: true } : {})}>
                        <summary className="list-none">
                          <dt className="font-medium text-sm text-gray-500 flex items-center cursor-pointer focus-visible:outline-none focus:ring focus:ring-indigo-200 gap-3">
                            <span>Présentation</span>
                            <div className="border-4 border-transparent border-l-gray-600 mr-2 group-open:rotate-90 transition-transform origin-left"></div>
                          </dt>
                        </summary>
                        <div>
                          <dd className=" text-sm mt-1 text-gray-900 whitespace-pre-line italic">{bio}</dd>
                        </div>
                      </details>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default MemberDetails;
